// ============================================================================
// RHCancelamentoAdmissao_Destinatarios.js
// Tela do RH: gerir QUEM recebe o e-mail quando um gerente pede o cancelamento
// de uma admissão. Adicionar usuário do portal, ativar/inativar (nunca apaga —
// histórico preservado). A lista de "adicionar" traz só usuários ATIVOS com
// perfil RH, Admin ou Diretoria (filtro no backend). Os destinatários vivem na
// tabela ÚNICA de RH (dtbRhDestinatarioEmail, APLICACAO='cancelamento-admissao').
// Renderizada pelo mini-hub RHCancelamentoAdmissao.
// ============================================================================

import { useEffect, useState, useCallback } from 'react';
import { Mail, UserPlus, Power, PowerOff, AlertCircle } from 'lucide-react';
import { estilos, BadgeStatus } from '../../processoseletivo/psUI';

const COR = '#e11d48';
const BASE = '/api/rh/cancelamento-admissao';

function CancelamentoDestinatarios() {
  const [destinatarios, setDestinatarios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [novoUsuarioId, setNovoUsuarioId] = useState('');
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const token = localStorage.getItem('authToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [rd, ru] = await Promise.all([
        fetch(`${BASE}/destinatarios`, { headers }),
        fetch(`${BASE}/usuarios`, { headers }),
      ]);
      if (!rd.ok || !ru.ok) throw new Error('Falha ao carregar dados.');
      setDestinatarios(await rd.json());
      setUsuarios(await ru.json());
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const adicionar = async () => {
    if (!novoUsuarioId) return;
    setSalvando(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${BASE}/destinatarios`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ usuarioId: Number(novoUsuarioId) }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.message || 'Erro ao adicionar.');
      }
      setNovoUsuarioId('');
      await carregar();
    } catch (e) {
      alert(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternar = async (id, ativoAtual) => {
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${BASE}/destinatarios/${id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ativo: ativoAtual ? 0 : 1 }),
      });
      if (!resp.ok) throw new Error('Erro ao atualizar.');
      await carregar();
    } catch (e) {
      alert(e.message);
    }
  };

  const idsNaLista = new Set(destinatarios.map((d) => d.USUARIO_ID));
  const disponiveis = usuarios.filter((u) => !idsNaLista.has(u.ID));

  const input = { padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, color: '#0f172a', minWidth: 280 };

  return (
    <div style={{ maxWidth: 780 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ background: `${COR}15`, color: COR, padding: 12, borderRadius: 12, display: 'flex' }}>
          <Mail size={24} />
        </div>
        <div>
          <h3 style={estilos.tituloSecao}>Destinatários dos avisos</h3>
          <p style={estilos.subtituloSecao}>Quem recebe o e-mail quando um gerente pede o término de um contrato de experiência.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={input} value={novoUsuarioId} onChange={(e) => setNovoUsuarioId(e.target.value)}>
          <option value="">— Escolha usuário para adicionar —</option>
          {disponiveis.map((u) => (
            <option key={u.ID} value={u.ID}>{u.NOME_COMPLETO} · {u.PERFIL}</option>
          ))}
        </select>
        <button
          onClick={adicionar}
          disabled={!novoUsuarioId || salvando}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', borderRadius: 8, border: 'none',
            fontWeight: 700, fontSize: 14, color: '#fff',
            background: novoUsuarioId && !salvando ? COR : '#f1d5da',
            cursor: novoUsuarioId && !salvando ? 'pointer' : 'not-allowed',
          }}
        >
          <UserPlus size={16} /> Adicionar
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 24, color: '#64748b' }}>Carregando…</div>
      ) : erro ? (
        <div style={{ padding: 24, color: COR, display: 'flex', alignItems: 'center', gap: 8 }}><AlertCircle size={18} /> {erro}</div>
      ) : (
        <div style={{ ...estilos.tabelaWrap, overflowX: 'auto' }}>
          <table style={{ ...estilos.tabela, minWidth: 560 }}>
            <thead>
              <tr>
                <th style={estilos.th}>Usuário</th>
                <th style={estilos.th}>E-mail</th>
                <th style={estilos.th}>Perfil</th>
                <th style={estilos.th}>Situação</th>
                <th style={{ ...estilos.th, textAlign: 'right' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {destinatarios.map((d) => (
                <tr key={d.ID} style={{ opacity: d.ATIVO ? 1 : 0.6 }}>
                  <td style={estilos.td}><strong>{d.NOME_COMPLETO}</strong></td>
                  <td style={estilos.td}>{d.EMAIL || '—'}</td>
                  <td style={estilos.td}>{d.PERFIL}</td>
                  <td style={estilos.td}>
                    <BadgeStatus tom={d.ATIVO ? 'aprovado' : 'neutro'}>{d.ATIVO ? 'Ativo' : 'Inativo'}</BadgeStatus>
                  </td>
                  <td style={{ ...estilos.td, textAlign: 'right' }}>
                    <button
                      onClick={() => alternar(d.ID, d.ATIVO)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8,
                        border: `1px solid ${d.ATIVO ? '#e2e8f0' : COR}`, background: '#fff',
                        color: d.ATIVO ? '#64748b' : COR, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      {d.ATIVO ? <><PowerOff size={14} /> Inativar</> : <><Power size={14} /> Ativar</>}
                    </button>
                  </td>
                </tr>
              ))}
              {destinatarios.length === 0 && (
                <tr><td style={{ ...estilos.td, textAlign: 'center', color: '#94a3b8' }} colSpan={5}>Nenhum destinatário cadastrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CancelamentoDestinatarios;
